import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useConversas } from '@/conversas/ConversasProvider';
import { nomeDoContato } from '@/conversas/contato';
import { excluirGrupo } from '@/services/conversasService';

/**
 * Confirmação forte para apagar um grupo — o único ato irreversível da inbox.
 *
 * Digitar a palavra em vez de só clicar "Confirmar" é deliberado: o clique duplo
 * distraído é exatamente o gesto que se quer impedir, e um segundo botão não o
 * impede. Escrever EXCLUIR obriga a ler o que está sendo apagado.
 *
 * O diálogo NÃO é a proteção de segurança — quem recusa conversa de paciente e
 * usuário sem papel é a callable. Aqui é proteção contra o próprio gestor.
 */

const PALAVRA = 'EXCLUIR';

const MENSAGENS_ERRO = {
  'functions/permission-denied':
    'Recusado pelo servidor: só o gestor da clínica pode excluir, e só grupos.',
  'functions/not-found': 'Esta conversa já não existe.',
  'functions/invalid-argument': 'O servidor não aceitou os dados enviados.',
  'functions/internal': 'Não foi possível falar com o servidor. Tente de novo.',
};

export default function DialogoExcluirGrupo({ aberto, aoFechar, aoExcluir }) {
  const { clinicaId, conversa } = useConversas();
  const [confirmacao, setConfirmacao] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState(null);

  // Toda abertura começa limpa: a palavra digitada da vez anterior não pode
  // sobrar habilitando o botão de um grupo diferente.
  useEffect(() => {
    if (!aberto) return;
    setConfirmacao('');
    setErro(null);
  }, [aberto, conversa?.id]);

  if (!conversa) return null;

  const nome = nomeDoContato(conversa);
  const liberado = confirmacao.trim().toUpperCase() === PALAVRA;

  async function confirmar() {
    if (!liberado) return;
    setExcluindo(true);
    setErro(null);
    try {
      await excluirGrupo({ clinicaId, conversaId: conversa.id });
      aoExcluir();
    } catch (e) {
      console.error('[OdontoSDR] Falha ao excluir o grupo:', e?.code);
      setErro(MENSAGENS_ERRO[e?.code] ?? e?.message ?? 'Não foi possível excluir o grupo.');
      setExcluindo(false);
    }
  }

  return (
    <Dialogo titulo="Excluir grupo" aberto={aberto} aoFechar={aoFechar} largura={480}>
      <div className="dialogo-corpo">
        <p>
          Você está prestes a apagar o grupo <strong>{nome}</strong> e{' '}
          <strong>todas as mensagens dele</strong>.
        </p>

        <div className="aviso-destrutivo">
          <strong>Isto é permanente.</strong> Não há desfazer, não vai para lixeira e o
          histórico não é recuperável. O grupo no WhatsApp não é tocado — some daqui,
          não do aparelho.
        </div>

        <div className="campo">
          <label htmlFor="confirmar-exclusao">
            Digite <code>{PALAVRA}</code> para liberar o botão
          </label>
          <input
            id="confirmar-exclusao"
            type="text"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            placeholder={PALAVRA}
            autoComplete="off"
            disabled={excluindo}
          />
        </div>

        {erro && <p className="erro">{erro}</p>}

        <div className="dialogo-acoes">
          <button type="button" className="btn-texto" onClick={aoFechar} disabled={excluindo}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-destrutivo"
            onClick={confirmar}
            disabled={!liberado || excluindo}
            title={liberado ? undefined : `Digite ${PALAVRA} para liberar`}
          >
            {excluindo ? 'Excluindo…' : 'Excluir definitivamente'}
          </button>
        </div>
      </div>
    </Dialogo>
  );
}
